import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Check, Search, UserRound, X } from 'lucide-react';
import {
  PATHOLOGY_TYPES_CATALOG,
  PATHOLOGY_GROUP_LABELS,
  SignPathologyDeliverySchema,
  type Patient,
} from '@datazo/contracts';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { avatarGradient } from '@/components/ui/avatar';
import { SignaturePad } from '@/components/SignaturePad';
import { ApiClientError } from '@/shared/api/client';
import { initials } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { usePageTitle } from '@/shared/store/page';
import { useToast } from '@/shared/toast/toast-context';
import { usePatientsList } from '@/features/patients/api';
import { useSignPathologyDelivery } from './api';

const OTRO = '__otro__';

// Opciones del catálogo ordenadas por grupo (Gástricas, luego Colón) + "Otro".
const PATOLOGIA_OPTIONS = [
  ...PATHOLOGY_TYPES_CATALOG.map((p) => ({
    value: p.nombre,
    label: p.nombre,
    keywords: PATHOLOGY_GROUP_LABELS[p.grupo],
  })),
  { value: OTRO, label: 'Otro (especificar)…' },
];

export function PathologyNewPage(): JSX.Element {
  usePageTitle('Nueva entrega de patología', 'Documentos clínicos · Constancia de entrega');
  const navigate = useNavigate();
  const { showToast } = useToast();
  const signMutation = useSignPathologyDelivery();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [patologiaSel, setPatologiaSel] = useState('');
  const [patologiaOtro, setPatologiaOtro] = useState('');
  const [nombreReceptor, setNombreReceptor] = useState('');
  const [parentescoReceptor, setParentesco] = useState('');
  const [documentoReceptor, setDocReceptor] = useState('');
  const [firma, setFirma] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const patologia = patologiaSel === OTRO ? patologiaOtro.trim() : patologiaSel;

  const input = useMemo(() => {
    if (patient === null) return null;
    return {
      pacienteId: patient.id,
      patologia,
      nombreReceptor: nombreReceptor.trim(),
      parentescoReceptor: parentescoReceptor.trim(),
      documentoReceptor: documentoReceptor.trim(),
      firmaReceptor: firma ?? '',
    };
  }, [patient, patologia, nombreReceptor, parentescoReceptor, documentoReceptor, firma]);

  const parsed = input === null ? null : SignPathologyDeliverySchema.safeParse(input);
  const canSubmit = parsed?.success === true;

  const submit = async (): Promise<void> => {
    setServerError(null);
    if (parsed === null || !parsed.success) return;
    try {
      await signMutation.mutateAsync(parsed.data);
      showToast({
        kind: 'success',
        title: 'Constancia firmada',
        description: 'Quedó registrada. Puedes descargarla desde la lista.',
      });
      navigate('/patologia');
    } catch (err) {
      setServerError(
        err instanceof ApiClientError ? err.message : 'No fue posible firmar la constancia.',
      );
    }
  };

  const hoy = new Date().toLocaleDateString('es-CO');

  return (
    <div className="flex flex-col gap-5 max-w-3xl">
      <div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/patologia">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
        </Button>
      </div>

      {/* 1. Paciente */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>1 · Paciente</CardTitle>
            <CardDescription>Busca y selecciona el paciente del resultado.</CardDescription>
          </div>
        </CardHeader>
        <div className="p-6">
          {patient === null ? (
            <PatientPicker onPick={setPatient} />
          ) : (
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'w-11 h-11 rounded-md grid place-items-center font-bold text-white',
                  avatarGradient(patient.id),
                )}
              >
                {initials(patient.nombres, patient.apellidos)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-text">
                  {patient.nombres} {patient.apellidos}
                </div>
                <div className="text-text-muted text-xs font-mono">
                  {patient.tipoDoc} {patient.documento}
                </div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => setPatient(null)}>
                <X className="w-4 h-4" /> Cambiar
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* 2. Patología + receptor */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>2 · Datos de la entrega</CardTitle>
            <CardDescription>
              Tipo de patología y datos de quien recibe la constancia.
            </CardDescription>
          </div>
        </CardHeader>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <Label>Tipo de patología</Label>
            <SearchableSelect
              value={patologiaSel}
              onChange={setPatologiaSel}
              options={PATOLOGIA_OPTIONS}
              placeholder="Selecciona la patología…"
              clearable
            />
            {patologiaSel === OTRO && (
              <Input
                value={patologiaOtro}
                onChange={(e) => setPatologiaOtro(e.target.value)}
                placeholder="Escribe la patología"
                className="mt-1.5"
              />
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rec-nombre">Nombre de quien recibe</Label>
            <Input
              id="rec-nombre"
              value={nombreReceptor}
              onChange={(e) => setNombreReceptor(e.target.value)}
              placeholder="Nombre completo"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rec-parentesco">Parentesco o calidad</Label>
            <Input
              id="rec-parentesco"
              value={parentescoReceptor}
              onChange={(e) => setParentesco(e.target.value)}
              placeholder="Familiar, acudiente, tercero autorizado…"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rec-doc">Documento de identidad</Label>
            <Input
              id="rec-doc"
              value={documentoReceptor}
              onChange={(e) => setDocReceptor(e.target.value)}
              placeholder="Número de documento"
            />
          </div>
        </div>
      </Card>

      {/* 3. Documento (vista previa) */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>3 · Documento</CardTitle>
            <CardDescription>Léelo antes de firmar. La firma queda plasmada al confirmar.</CardDescription>
          </div>
        </CardHeader>
        <div className="p-6">
          <div className="border border-border rounded-md p-6 bg-white text-[13px] leading-relaxed text-text">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-extrabold text-[15px]">
                  CONSTANCIA DE ENTREGA DE RESULTADO DE PATOLOGÍA
                </div>
                <div className="text-text-muted text-[11px]">
                  Endomedical IPS · Gastroenterología y Endoscopia Digestiva Avanzada
                </div>
              </div>
              <img src="/logo-endomedical.png" alt="Endomedical" className="h-9 w-auto" />
            </div>
            <p className="mb-3">
              Se hace constar que el resultado de patología correspondiente al paciente{' '}
              <strong>{patient ? `${patient.nombres} ${patient.apellidos}` : '____________'}</strong>{' '}
              fue entregado en la fecha de hoy.
            </p>
            <p className="mb-3">
              <strong>Patología entregada:</strong> {patologia === '' ? '____________' : patologia}
            </p>
            <p className="mb-3">
              Yo, <strong>{nombreReceptor === '' ? '____________' : nombreReceptor}</strong>,
              identificado(a) con documento de identidad No.{' '}
              <strong>{documentoReceptor === '' ? '________' : documentoReceptor}</strong>, en calidad
              de <strong>{parentescoReceptor === '' ? '____________' : parentescoReceptor}</strong>{' '}
              (familiar, acudiente o tercero autorizado), manifiesto que recibo a satisfacción el
              resultado anteriormente descrito y me comprometo a hacerlo llegar al paciente y/o a su
              médico tratante para los fines médicos correspondientes.
            </p>
            <p className="mb-3">
              Con mi firma dejo constancia únicamente de la recepción del resultado de patología.
            </p>
            <p className="text-text-muted">Fecha: {hoy}</p>
          </div>
        </div>
      </Card>

      {/* 4. Firma */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>4 · Firma de quien recibe</CardTitle>
            <CardDescription>Firma en el recuadro para generar la constancia.</CardDescription>
          </div>
        </CardHeader>
        <div className="p-6 flex flex-col gap-3">
          <SignaturePad onChange={setFirma} placeholder="Firma de quien recibe" />
          {serverError !== null && (
            <div className="px-3 py-2 rounded-md bg-danger-soft text-red-800 text-sm border border-red-200 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{serverError}</span>
            </div>
          )}
          <div className="flex justify-end">
            <Button onClick={() => void submit()} disabled={!canSubmit || signMutation.isPending}>
              <Check className="w-4 h-4" />
              {signMutation.isPending ? 'Generando…' : 'Firmar y generar constancia'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Patient picker (búsqueda con debounce)                                     */
/* -------------------------------------------------------------------------- */

function PatientPicker({ onPick }: { onPick: (p: Patient) => void }): JSX.Element {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = usePatientsList({
    page: 1,
    pageSize: 8,
    ...(debounced.trim() === '' ? {} : { q: debounced.trim() }),
  });
  const patients = data?.items ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre o documento…"
          className="pl-9"
          autoFocus
        />
      </div>
      {isLoading ? (
        <div className="text-text-muted text-sm px-1">Buscando…</div>
      ) : patients.length === 0 ? (
        <div className="text-text-muted text-sm px-1 flex items-center gap-2">
          <UserRound className="w-4 h-4" />
          {debounced.trim() === '' ? 'Escribe para buscar un paciente.' : 'Sin coincidencias.'}
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {patients.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onPick(p)}
              className="flex items-center gap-3 p-2.5 rounded-md border border-border hover:border-primary hover:bg-primary-soft/40 text-left transition-colors"
            >
              <div
                className={cn(
                  'w-9 h-9 rounded-md grid place-items-center font-bold text-xs text-white flex-shrink-0',
                  avatarGradient(p.id),
                )}
              >
                {initials(p.nombres, p.apellidos)}
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-text truncate">
                  {p.nombres} {p.apellidos}
                </div>
                <div className="text-text-muted text-xs font-mono">
                  {p.tipoDoc} {p.documento}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
